import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Wrench,
  ExternalLink
} from 'lucide-react';
import { companyInfo } from '../data/mock';

const HandymanServices = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', postcode: '', message: '', honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: `[Handyman Enquiry] ${formData.message}`,
          trade_type: 'handyman',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/handyman-services'
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Shelving installation',
    'Curtain poles and blinds',
    'Furniture assembly',
    'Picture and mirror hanging',
    'TV wall mounting',
    'Door adjustments and repairs',
    'Lock changes and ironmongery',
    'Handle replacements',
    'Boxing-in pipework',
    'Sealant renewal',
    'Fixture repairs',
    'General property repairs',
    'Smoke alarm fitting',
    'Towel rail installation',
    'Letterbox repairs',
    'Minor tiling repairs'
  ];

  const benefits = [
    'Multi-skilled handyman service',
    'Professional finish on all work',
    'Clear, upfront pricing',
    'Clean and tidy working',
    '£5 million public liability insurance',
    '12-month workmanship guarantee'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Handyman Services Manchester | Property Repairs | Expert Property Maintenance</title>
        <meta name="description" content="Handyman services in Manchester for landlords, letting agents and homeowners. Shelving, fixtures, repairs, furniture assembly, door repairs, general maintenance. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/handyman-services" />
      </Helmet>

      <Header variant="default" companyPhone={companyInfo.phone} />

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Wrench className="w-4 h-4" />
              Handyman Services
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Handyman Services Manchester
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Professional handyman services for landlords, letting agents and homeowners across Greater Manchester. 
              From shelving and fixtures to general repairs and odd jobs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">
                  Request a Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                Professional Handyman Services
              </h2>
              <p className="text-lg text-brand-slate mb-6">
                A good handyman can tackle a wide range of tasks — the jobs that don't need a specialist 
                tradesperson but do need completing properly. Shelving that's level, doors that close 
                correctly, fixtures installed securely.
              </p>
              <p className="text-brand-slate mb-6">
                Expert Property Maintenance provides handyman services as part of our broader property 
                maintenance offering. Whether you're a landlord needing work done between tenancies, 
                a letting agent with a list of odd jobs, or a homeowner wanting things sorted around the house.
              </p>
              <p className="text-brand-slate mb-8">
                Our handyman work is delivered with the same standards as our specialist trades — properly 
                done, cleaned up afterwards, and guaranteed.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Expert Handyman Brand Link */}
              <div className="mt-8 p-4 bg-brand-grey border border-slate-200 rounded-xl">
                <p className="text-sm text-brand-slate mb-2">Our specialist handyman brand:</p>
                <a 
                  href="https://expert-handyman.co.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-brand-yellow hover:text-yellow-300 font-semibold"
                >
                  Expert Handyman <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
            
            <div className="bg-brand-grey rounded-2xl p-8">
              <h3 className="text-xl font-bold text-brand-navy mb-6">Handyman Services Include</h3>
              <div className="grid grid-cols-2 gap-3">
                {services.map((service) => (
                  <div key={service} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow flex-shrink-0" />
                    <span className="text-sm text-brand-slate">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Landlords */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-navy rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Handyman Services for Landlords
                </h2>
                <p className="text-slate-300 mb-6">
                  Rental properties accumulate small jobs — a loose handle, shelves to install for a new 
                  tenant, blinds to fit. Our handyman service lets you tackle multiple jobs in a single visit, 
                  making it cost-effective to address all those minor issues.
                </p>
                <p className="text-slate-400 text-sm mb-6">
                  Combine handyman work with our other services for a complete maintenance solution.
                </p>
                <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                  <a href="/landlord-services">
                    All Landlord Services
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">End of Tenancy Jobs</h4>
                  <p className="text-sm text-slate-400">Addressing odd jobs before new tenants move in</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Tenant Requests</h4>
                  <p className="text-sm text-slate-400">Fitting shelves, blinds and fixtures as requested</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Property Improvements</h4>
                  <p className="text-sm text-slate-400">Small upgrades to maintain property standards</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/carpentry-joinery" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Carpentry & Joinery</h4>
              <p className="text-sm text-brand-slate">Specialist woodwork</p>
            </a>
            <a href="/reactive-property-maintenance" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Reactive Maintenance</h4>
              <p className="text-sm text-brand-slate">Urgent repairs</p>
            </a>
            <a href="/void-property-works" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Void Property Works</h4>
              <p className="text-sm text-brand-slate">Between tenancies</p>
            </a>
            <a href="/property-refurbishment" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Property Refurbishment</h4>
              <p className="text-sm text-brand-slate">Complete renovations</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Get a Handyman Quote</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us what you need doing and we will provide a quote.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch with a quote.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Request a Quote</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Postcode</label>
                      <Input value={formData.postcode} onChange={(e) => setFormData({...formData, postcode: e.target.value})} placeholder="e.g., M1 1AA" className="h-12" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">What do you need doing? *</label>
                      <Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="List the jobs you need help with..." rows={4} />
                    </div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default HandymanServices;
